// Automatic vendor follow-up on a PO (jobq-api po_followups.py, 2026-10-08).
// GET /purchase_orders/{id}/follow_up returns {state, detail, mode, send_at, mailq, ...};
// followUpSummary turns it into the one sentence the PO page shows. Pure: tested in
// poFollowUp.test.js.

const CT = 'America/Chicago';

export function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    timeZone: CT, weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function who(m) {
  return (m && (m.from_name || m.from_address)) || 'The vendor';
}

// {tone, text, note}: tone is a Bootstrap colour; note is an optional second line.
export function followUpSummary(v) {
  if (!v) return { tone: 'secondary', text: '', note: null };
  const responses = (v.mailq && v.mailq.responses) || [];
  const autoReplies = (v.mailq && v.mailq.auto_replies) || [];
  const shadow = v.mode === 'shadow';
  const reply = responses[0];
  const replyLine = reply
    ? `${who(reply)} replied ${fmtWhen(reply.received_at)}: “${reply.subject || '(no subject)'}”`
    : null;
  const ooo = autoReplies.length ? `Auto-reply received ${fmtWhen(autoReplies[0].received_at)} (out of office?). That doesn't count as an answer.` : null;
  // What the last hourly run hit, when that was a problem the live answer can't show.
  const last = v.last_run || null;
  const lastProblem = last && ['send_failed', 'check_failed'].includes(last.state)
    ? `Last hourly check (${fmtWhen(last.checked_at)}): ${last.detail || last.state}` : null;

  switch (v.state) {
    case 'responded':
      return { tone: 'success', text: replyLine || v.detail, note: null };
    case 'manual':
      return { tone: 'success', text: 'Someone already followed up from MailQ, so JobQ won\'t.', note: null };
    case 'sent': {
      const f = v.follow_up || {};
      if (f.status === 'unknown') {
        return {
          tone: 'warning',
          text: `The follow-up to ${f.to_email || 'the vendor'} may have gone out ${fmtWhen(f.sent_at)} (SES didn't confirm). JobQ won't resend it; check before sending one by hand.`,
          note: replyLine,
        };
      }
      return {
        tone: 'primary',
        text: `Automatic follow-up sent ${fmtWhen(f.sent_at)} to ${f.to_email || 'the vendor'}.`,
        note: replyLine,
      };
    }
    case 'waiting':
      if (v.detail === 'Due; sends in business hours') {
        return {
          tone: 'info',
          text: `No reply yet. The follow-up goes out ${fmtWhen(v.send_at)} (sent between 8 AM and 5 PM).`
            + (shadow ? ' Shadow mode: it will be logged, not emailed.' : ''),
          note: ooo || lastProblem,
        };
      }
      if (v.detail === 'Automatic follow-ups are off') {
        return { tone: 'secondary', text: 'Automatic follow-ups are off.', note: null };
      }
      return {
        tone: 'info',
        text: `No reply in MailQ yet. If the vendor hasn't answered by ${fmtWhen(v.send_at)}, JobQ `
          + (shadow ? 'will log a follow-up (shadow mode: nothing is emailed yet).' : 'emails them one follow-up.'),
        note: ooo || lastProblem,
      };
    case 'would_send':
      if (shadow) {
        return {
          tone: 'warning',
          text: v.would_send_at
            ? `No reply after 2 business days. Shadow mode: JobQ logged ${fmtWhen(v.would_send_at)} that it would send a follow-up; nothing was emailed.`
            : 'No reply after 2 business days. Shadow mode: the next hourly check logs a follow-up; nothing is emailed.',
          note: ooo,
        };
      }
      return { tone: 'warning', text: 'No reply after 2 business days. The follow-up goes out at the next hourly check.', note: ooo || lastProblem };
    case 'check_failed':
      return { tone: 'warning', text: v.detail || 'Couldn\'t check MailQ, so nothing was sent.', note: null };
    case 'send_failed':
      return { tone: 'danger', text: `The follow-up didn't send: ${v.detail || 'unknown reason'}`, note: null };
    case 'expired':
      return { tone: 'secondary', text: v.detail || 'Too late for an automatic follow-up.', note: null };
    case 'skipped':
    default:
      return { tone: 'secondary', text: `No automatic follow-up: ${v.detail || 'not eligible'}.`, note: replyLine };
  }
}

// The toggle only means something for a PO that could still be followed up.
export function canToggle(v) {
  return !!v && !['sent', 'responded', 'manual', 'expired'].includes(v.state);
}
